import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Avatar,
  IconButton,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Chip
} from '@mui/material';
import {
  CallEnd as CallEndIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  VolumeUp as VolumeUpIcon,
  VolumeOff as VolumeOffIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { connect, createLocalVideoTrack, createLocalAudioTrack } from 'twilio-video';
import { getVideoToken, endVideoCall, startVideoCall } from '../../services/healinApi';
import { useAuth } from '../../auth/AuthContext';

/**
 * TwilioVideo Component
 * 
 * This component provides a video call interface using Twilio Video.
 * It handles connecting to a room, managing video/audio tracks, and UI controls.
 * 
 * @param {Object} props
 * @param {Object} props.appointment - The appointment object
 * @param {Function} props.onEndCall - Callback when the video call is ended
 * @param {String} props.doctorName - The name of the doctor
 * @param {Boolean} props.autoStart - Whether to automatically start the call
 */
function TwilioVideo({ 
  appointment, 
  onEndCall, 
  doctorName,
  autoStart = true 
}) {
  console.log('TwilioVideo component mounted/re-rendered with:', { 
    appointmentId: appointment?.requestId, 
    autoStart, 
    doctorName 
  });
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [participants, setParticipants] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localTracks, setLocalTracks] = useState([]);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isSpeakerEnabled, setIsSpeakerEnabled] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState('idle');

  const localVideoRef = useRef();
  const remoteVideoRef = useRef();
  const callStartTime = useRef(null);
  const durationInterval = useRef(null);
  
  // Add connection tracking ref to prevent duplicate connections from React Strict Mode
  const connectionAttemptRef = useRef(false);
  const componentMountedRef = useRef(true);

  // Current doctor info from authentication
  const currentDoctor = {
    id: user?.username || 'unknown-doctor',
    name: doctorName || user?.attributes?.name || user?.name || user?.username || 'Doctor'
  };

  // Format call duration
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Start duration timer
  const startDurationTimer = useCallback(() => {
    if (durationInterval.current) return;
    
    callStartTime.current = Date.now();
    durationInterval.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - callStartTime.current) / 1000);
      setCallDuration(elapsed);
    }, 1000);
  }, []);

  // Stop duration timer
  const stopDurationTimer = useCallback(() => {
    if (durationInterval.current) {
      clearInterval(durationInterval.current);
      durationInterval.current = null;
    }
  }, []);

  // Handle participant connection
  const participantConnected = useCallback((participant) => {
    console.log('Participant connected:', participant.identity);
    setParticipants(prevParticipants => new Map(prevParticipants.set(participant.sid, participant)));
    
    // Attach participant tracks
    participant.tracks.forEach(publication => {
      if (publication.isSubscribed) {
        const track = publication.track;
        if (track.kind === 'video' && remoteVideoRef.current) {
          track.attach(remoteVideoRef.current);
        }
      }
    });

    // Handle track subscriptions
    participant.on('trackSubscribed', track => {
      console.log('Track subscribed:', track.kind);
      if (track.kind === 'video' && remoteVideoRef.current) {
        track.attach(remoteVideoRef.current);
      }
    });

    participant.on('trackUnsubscribed', track => {
      console.log('Track unsubscribed:', track.kind);
      track.detach();
    });
  }, []);

  // Handle participant disconnection
  const participantDisconnected = useCallback((participant) => {
    console.log('Participant disconnected:', participant.identity);
    setParticipants(prevParticipants => {
      const newParticipants = new Map(prevParticipants);
      newParticipants.delete(participant.sid);
      return newParticipants;
    });
  }, []);

  // Connect to video room
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const connectToRoom = useCallback(async () => {
    // Enhanced guards against multiple simultaneous connection attempts
    if (room || 
        connectionStatus === 'connecting' || 
        connectionStatus === 'connected' ||
        connectionAttemptRef.current ||
        !componentMountedRef.current) {
      console.log('Connection already in progress or established, skipping...', {
        hasRoom: !!room,
        connectionStatus,
        connectionAttemptInProgress: connectionAttemptRef.current,
        componentMounted: componentMountedRef.current
      });
      return;
    }

    try {
      // Set connection attempt flag to prevent duplicate attempts
      connectionAttemptRef.current = true;
        setLoading(true);
        setError(null);
      setConnectionStatus('connecting');

      console.log('Getting video token for appointment:', appointment.requestId);
      
      // Get video token (this will create room if it doesn't exist)
      let tokenResponse;
      try {
        tokenResponse = await getVideoToken({
          userId: appointment.userId,
          requestId: appointment.requestId
        });
      } catch (tokenError) {
        console.warn('Failed to get token, trying to start video call first:', tokenError.message);

        // If token fails, try to start the video call first (creates the room)
        await startVideoCall({
          userId: appointment.userId,
          requestId: appointment.requestId,
          assignedDoctorId: currentDoctor.id
        });
        
        // Now try to get the token again
        tokenResponse = await getVideoToken({
          userId: appointment.userId,
          requestId: appointment.requestId
        });
      }

      console.log('Video token received, connecting to room:', tokenResponse.roomSid);

      // Create local tracks with fallback options
      const tracks = [];
      let audioTrack = null;
      let videoTrack = null;

      try {
        // Try to create audio track
        audioTrack = await createLocalAudioTrack();
        tracks.push(audioTrack);
        console.log('Audio track created successfully');
      } catch (audioError) {
        console.warn('Failed to create audio track:', audioError.message);
        setError('Microphone access denied. You can still join audio-only or video-only.');
      }

      try {
        // Try to create video track
        videoTrack = await createLocalVideoTrack();
        tracks.push(videoTrack);
        console.log('Video track created successfully');

        // Attach local video track
        if (localVideoRef.current) {
          videoTrack.attach(localVideoRef.current);
        }
      } catch (videoError) {
        console.warn('Failed to create video track:', videoError.message);
        if (!audioTrack) {
          throw new Error('Both camera and microphone access denied. Please check your browser permissions.');
        }
        setError('Camera access denied. Continuing with audio-only call.');
        }

      setLocalTracks(tracks);

      // Connect to room with available tracks
      const connectedRoom = await connect(tokenResponse.token, {
        name: tokenResponse.roomSid,
        tracks: tracks,
        audio: !!audioTrack,
        video: !!videoTrack,
        dominantSpeaker: true
        });

      setRoom(connectedRoom);
      setConnectionStatus('connected');
      connectionAttemptRef.current = false; // Reset connection attempt flag on success
      startDurationTimer();

      // Update track states
      setIsAudioEnabled(!!audioTrack);
      setIsVideoEnabled(!!videoTrack);

      console.log('Connected to room:', connectedRoom.sid);

      // Handle existing participants
      connectedRoom.participants.forEach(participantConnected);

      // Handle new participants
      connectedRoom.on('participantConnected', participantConnected);
      connectedRoom.on('participantDisconnected', participantDisconnected);
        
      // Handle room disconnection
      connectedRoom.on('disconnected', (room, error) => {
        console.log('Disconnected from room:', error);
        setConnectionStatus('disconnected');
        connectionAttemptRef.current = false; // Reset connection attempt flag
        stopDurationTimer();
        
        if (error) {
          setError(`Call ended: ${error.message}`);
        }
      });

      } catch (err) {
      console.error('Error connecting to video room:', err);
      let errorMessage = 'Failed to join video call';
      
      if (err.message.includes('Permission denied') || err.message.includes('NotAllowedError')) {
        errorMessage = 'Camera/microphone access denied. Please check your browser permissions and try again.';
      } else if (err.message.includes('NotFoundError')) {
        errorMessage = 'No camera or microphone found. Please check your device connections.';
      } else if (err.message.includes('Room not found')) {
        errorMessage = 'Video room expired or not found. Please try starting the call again.';
      } else if (err.message.includes('duplicate identity')) {
        errorMessage = 'Connection conflict detected. Please refresh the page and try again.';
        console.warn('Duplicate identity error - likely caused by multiple connection attempts');
      } else if (err.message.includes('TwilioError')) {
        errorMessage = `Video service error: ${err.message}. Please try again.`;
      } else {
        errorMessage = `Failed to join video call: ${err.message}`;
      }
      
      setError(errorMessage);
      setConnectionStatus('error');
      connectionAttemptRef.current = false; // Reset connection attempt flag on error
    } finally {
      setLoading(false);
    }
  }, [appointment, participantConnected, participantDisconnected, startDurationTimer, stopDurationTimer, currentDoctor.id]);

  // Disconnect from room
  const disconnectFromRoom = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Disconnecting from video room...');
      
      // End the video call via API
      await endVideoCall({
        userId: appointment.userId,
        requestId: appointment.requestId,
        assignedDoctorId: currentDoctor.id
      });

      // Disconnect from Twilio room
      if (room) {
        console.log('Disconnecting Twilio room...');
        room.disconnect();
      }
      
      // Clean up local tracks
      localTracks.forEach(track => {
        track.stop();
        track.detach();
      });

      // Reset all states
      setLocalTracks([]);
      setRoom(null);
      setParticipants(new Map());
      setConnectionStatus('disconnected');
      setIsAudioEnabled(true);
      setIsVideoEnabled(true);
      connectionAttemptRef.current = false; // Reset connection attempt flag
      stopDurationTimer();
      
      // Notify parent component
      if (onEndCall) {
        onEndCall();
      }

    } catch (err) {
      console.error('Error ending video call:', err);
      setError(`Failed to end call: ${err.message}`);
    } finally {
      setLoading(false);
        }
  }, [room, localTracks, appointment, currentDoctor.id, onEndCall, stopDurationTimer]);

  // Toggle audio
  const toggleAudio = useCallback(() => {
    const audioTrack = localTracks.find(track => track.kind === 'audio');
    if (audioTrack) {
      if (isAudioEnabled) {
        audioTrack.disable();
        } else {
        audioTrack.enable();
      }
      setIsAudioEnabled(!isAudioEnabled);
    }
  }, [localTracks, isAudioEnabled]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    const videoTrack = localTracks.find(track => track.kind === 'video');
    if (videoTrack) {
      if (isVideoEnabled) {
        videoTrack.disable();
        } else {
        videoTrack.enable();
      }
      setIsVideoEnabled(!isVideoEnabled);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localTracks, isVideoEnabled]);

  // Check if we have any working tracks
  const hasVideoTrack = localTracks.some(track => track.kind === 'video');
  const hasAudioTrack = localTracks.some(track => track.kind === 'audio');
        
  // Toggle speaker (placeholder - actual implementation depends on device capabilities)
  const toggleSpeaker = useCallback(() => {
    setIsSpeakerEnabled(!isSpeakerEnabled);
    // Note: Actual speaker toggle would require additional WebRTC APIs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpeakerEnabled]);
        
  // Auto-connect on component mount with proper guards
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Mark component as mounted
    componentMountedRef.current = true;
    
    // Enhanced guard against double connection - additional checks for React Strict Mode
    if (autoStart && 
        appointment && 
        appointment.requestId && 
        appointment.userId && 
        !room && 
        connectionStatus === 'idle' &&
        !connectionAttemptRef.current &&
        componentMountedRef.current) {
      console.log('Starting initial connection to video room...', {
        appointmentId: appointment.requestId,
        userId: appointment.userId,
        connectionStatus,
        hasRoom: !!room,
        connectionAttemptInProgress: connectionAttemptRef.current
      });
      
      // Small delay to prevent React Strict Mode double execution
      const timeoutId = setTimeout(() => {
        if (componentMountedRef.current && !connectionAttemptRef.current) {
          connectToRoom();
      }
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
      };
    } else {
      console.log('Skipping connection due to conditions:', {
        autoStart,
        hasAppointment: !!appointment,
        hasRequestId: !!appointment?.requestId,
        hasUserId: !!appointment?.userId,
        hasRoom: !!room,
        connectionStatus,
        connectionAttemptInProgress: connectionAttemptRef.current,
        componentMounted: componentMountedRef.current
      });
    }
  }, [autoStart, appointment?.requestId, appointment?.userId]); // Only depend on essential props

  // Separate cleanup effect
  useEffect(() => {
    return () => {
      console.log('TwilioVideo component unmounting, cleaning up...');
      componentMountedRef.current = false;
      connectionAttemptRef.current = false;
      
    if (room) {
      room.disconnect();
    }
    localTracks.forEach(track => {
      track.stop();
        track.detach();
    });
      stopDurationTimer();
    };
  }, []); // Empty dependency array for cleanup only

  if (loading && connectionStatus === 'connecting') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        bgcolor: 'background.default'
      }}>
        <CircularProgress size={60} sx={{ mb: 3 }} />
        <Typography variant="h6" gutterBottom>
          Connecting to video call...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we establish the connection
        </Typography>
      </Box>
    );
  }

  // Request permissions helper
  const requestPermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Request both camera and microphone permissions
      await navigator.mediaDevices.getUserMedia({ 
        video: true, 
        audio: true 
      }).then(stream => {
        // Stop the stream immediately as we just needed permissions
        stream.getTracks().forEach(track => track.stop());
      });
      
      // Try connecting again
      await connectToRoom();
      
    } catch (err) {
      console.error('Permission request failed:', err);
      setError('Permission denied. Please allow camera and microphone access in your browser settings.');
    } finally {
      setLoading(false);
    }
  };

  if (error) {
    const isPermissionError = error.includes('Permission denied') || error.includes('access denied');
    const isRoomError = error.includes('Room not found') || error.includes('expired') || error.includes('Video service error');
    const isDuplicateIdentity = error.includes('duplicate identity') || error.includes('Connection conflict');
    
    return (
      <Box sx={{ 
        p: 3, 
        height: '100vh', 
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
          {error}
        </Alert>
        
        {isPermissionError && (
          <Box sx={{ mb: 3, textAlign: 'center', maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Camera/Microphone Access Required
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              To join the video call, please allow access to your camera and microphone.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Look for a camera/microphone icon in your browser's address bar and click "Allow".
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Chrome/Edge:</strong> Click the camera icon in the address bar
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Firefox:</strong> Click the shield icon and select "Allow"
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Safari:</strong> Go to Safari → Settings → Websites → Camera/Microphone
            </Typography>
          </Box>
        )}

        {isRoomError && !isDuplicateIdentity && (
          <Box sx={{ mb: 3, textAlign: 'center', maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Video Room Issue
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The video room may have expired or encountered an error. This can happen if the call was inactive for too long.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please try connecting again to create a new room.
            </Typography>
          </Box>
        )}

        {isDuplicateIdentity && (
          <Box sx={{ mb: 3, textAlign: 'center', maxWidth: 500 }}>
            <Typography variant="h6" gutterBottom>
              Connection Conflict
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Multiple connection attempts detected. This can happen when the page loads multiple times or React Strict Mode is active.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please refresh the page and try again. Make sure to close any other tabs with this video call open.
            </Typography>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isPermissionError && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={requestPermissions}
              disabled={loading}
            >
              {loading ? 'Requesting...' : 'Grant Permissions'}
            </Button>
          )}
          {isRoomError && !isDuplicateIdentity && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={connectToRoom}
              disabled={loading}
            >
              {loading ? 'Connecting...' : 'Retry Connection'}
            </Button>
          )}
          {isDuplicateIdentity && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          )}
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />}
            onClick={onEndCall}
          >
            Back to Appointment
        </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      height: '100vh', 
      bgcolor: '#1a1a1a', 
      color: 'white',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <Paper sx={{ 
        p: 2, 
        bgcolor: 'rgba(0,0,0,0.8)', 
        color: 'white',
        borderRadius: 0
      }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            <IconButton 
              onClick={disconnectFromRoom} 
              sx={{ color: 'white' }}
              disabled={loading}
            >
              <ArrowBackIcon />
            </IconButton>
          </Grid>
          <Grid item xs>
            <Typography variant="h6">
              {hasVideoTrack ? 'Video Consultation' : 'Audio Consultation'}
            </Typography>
            <Typography variant="body2" color="grey.300">
              {appointment.patientName || `Patient ${appointment.userId?.slice(-8)}`} • {formatDuration(callDuration)}
            </Typography>
          </Grid>
          <Grid item>
            <Chip 
              label={connectionStatus === 'connected' ? 'Connected' : connectionStatus}
              color={connectionStatus === 'connected' ? 'success' : 'default'}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Audio-only notification */}
      {connectionStatus === 'connected' && !hasVideoTrack && (
        <Alert 
          severity="info" 
      sx={{ 
            borderRadius: 0,
            bgcolor: 'info.dark',
            color: 'white',
            '& .MuiAlert-icon': { color: 'white' }
          }}
        >
          Running in audio-only mode. Video camera not available.
        </Alert>
      )}

      {/* Video Area */}
      <Box sx={{ flex: 1, position: 'relative', bgcolor: '#000' }}>
        {/* Remote Video (Main) */}
        <Box sx={{ 
          width: '100%', 
          height: '100%', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          position: 'relative'
        }}>
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          
          {participants.size === 0 && (
            <Box sx={{ 
              position: 'absolute',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'white'
            }}>
              <Avatar sx={{ width: 120, height: 120, mb: 2, bgcolor: 'primary.main' }}>
                <PersonIcon sx={{ fontSize: 60 }} />
              </Avatar>
              <Typography variant="h6">
                Waiting for {appointment.patientName || 'patient'} to join...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Local Video (Picture-in-Picture) */}
        <Card sx={{ 
          position: 'absolute', 
          top: 16,
          right: 16,
          width: 200, 
          height: 150, 
          bgcolor: '#333',
          border: '2px solid white'
        }}>
          <CardContent sx={{ p: 0, height: '100%', position: 'relative' }}>
            {hasVideoTrack ? (
              <video
          ref={localVideoRef} 
                autoPlay
                playsInline
                muted
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover' 
          }}
        />
            ) : (
              <Box sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: '#222'
              }}>
                <Avatar sx={{ width: 60, height: 60, bgcolor: 'primary.main' }}>
                  <PersonIcon sx={{ fontSize: 30 }} />
                </Avatar>
              </Box>
            )}
            
            <Box sx={{ 
              position: 'absolute',
              bottom: 4,
              left: 4,
              bgcolor: 'rgba(0,0,0,0.7)',
              px: 1,
              borderRadius: 1
            }}>
              <Typography variant="caption" color="white">
                {currentDoctor.name}
              </Typography>
            </Box>
            
            {/* Status indicators */}
            <Box sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              display: 'flex',
              gap: 0.5
            }}>
              {!hasAudioTrack && (
                <Chip 
                  icon={<MicOffIcon sx={{ fontSize: 12 }} />}
                  label="No Mic"
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: 10,
                    bgcolor: 'error.main',
                    color: 'white'
                  }}
                />
              )}
              {!hasVideoTrack && (
                <Chip 
                  icon={<VideocamOffIcon sx={{ fontSize: 12 }} />}
                  label="No Cam"
                  size="small"
                  sx={{ 
                    height: 20, 
                    fontSize: 10,
                    bgcolor: 'error.main',
                    color: 'white'
                  }}
                />
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
      
      {/* Controls */}
      <Paper sx={{ 
        p: 2, 
        bgcolor: 'rgba(0,0,0,0.9)', 
        borderRadius: 0,
          display: 'flex', 
          justifyContent: 'center', 
        gap: 2
      }}>
        <IconButton 
          onClick={toggleAudio} 
          disabled={!hasAudioTrack}
          sx={{ 
            bgcolor: hasAudioTrack 
              ? (isAudioEnabled ? 'grey.700' : 'error.main')
              : 'grey.800',
            color: hasAudioTrack ? 'white' : 'grey.500',
            '&:hover': {
              bgcolor: hasAudioTrack 
                ? (isAudioEnabled ? 'grey.600' : 'error.dark')
                : 'grey.800'
            }
          }}
        >
          {hasAudioTrack && isAudioEnabled ? <MicIcon /> : <MicOffIcon />}
        </IconButton>
        
        <IconButton 
          onClick={toggleVideo} 
          disabled={!hasVideoTrack}
          sx={{ 
            bgcolor: hasVideoTrack 
              ? (isVideoEnabled ? 'grey.700' : 'error.main')
              : 'grey.800',
            color: hasVideoTrack ? 'white' : 'grey.500',
            '&:hover': {
              bgcolor: hasVideoTrack 
                ? (isVideoEnabled ? 'grey.600' : 'error.dark')
                : 'grey.800'
            }
          }}
        >
          {hasVideoTrack && isVideoEnabled ? <VideocamIcon /> : <VideocamOffIcon />}
        </IconButton>
        
        <IconButton 
          onClick={toggleSpeaker}
          sx={{ 
            bgcolor: 'grey.700',
            color: 'white',
            '&:hover': {
              bgcolor: 'grey.600'
            }
          }}
        >
          {isSpeakerEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
        </IconButton>

        <Button
          variant="contained"
          color="error"
          startIcon={<CallEndIcon />}
          onClick={disconnectFromRoom}
          disabled={loading}
          sx={{ 
            borderRadius: 3,
            px: 3
          }}
        >
          End Call
        </Button>
      </Paper>
    </Box>
  );
}

export default TwilioVideo; 